import React, { useEffect } from 'react'
import {
  Grid,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Stack,
  FormHelperText,
  FormLabel,
} from '@chakra-ui/react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FormControl } from '@/components/FormControl'
import { FreeTrialFormInput } from '../../types'
import { TaxIdType, BranchType } from './types'
import { formGridStyles, fullWidthStyles } from './styles'
import { TAX, TAX_ID_CONFIG, BRANCH_CONFIG } from './constants'

export const CompanyTaxInvoiceForm = (): React.ReactElement => {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<FreeTrialFormInput & any>()
  const { t } = useTranslation(['freeTrial'])

  const taxIdType = useWatch({ control, name: 'tax_id_type' })
  const branchType = useWatch({ control, name: 'branch_type' })

  useEffect(() => {
    if (branchType !== BranchType.Branch) {
      setValue('branch_name', '')
    }
  }, [branchType, setValue])

  return (
    <Grid {...formGridStyles}>
      <FormControl errorMsg={errors.email?.message} isRequired>
        <FormLabel fontWeight="normal">
          {t('form.companyTaxInvoice.email')}
        </FormLabel>
        <Input
          type="email"
          placeholder={t('form.companyTaxInvoice.emailPlaceholder')}
          size="lg"
          {...register('email')}
        />
      </FormControl>

      <FormControl errorMsg={errors.phone?.message} isRequired>
        <FormLabel fontWeight="normal">
          {t('form.companyTaxInvoice.phone')}
        </FormLabel>
        <Input
          type="tel"
          placeholder={t('form.companyTaxInvoice.phonePlaceholder')}
          size="lg"
          {...register('phone')}
        />
      </FormControl>

      <FormControl sx={fullWidthStyles}>
        <FormLabel fontWeight="normal">
          {t('form.companyTaxInvoice.taxIdType')}
        </FormLabel>
        <RadioGroup
          onChange={(val) => setValue('tax_id_type', val as TaxIdType)}
          value={taxIdType}
        >
          <HStack spacing={4}>
            {TAX_ID_CONFIG.map((option) => (
              <Radio key={option.value} value={option.value}>
                {t(option.labelKey)}
              </Radio>
            ))}
          </HStack>
        </RadioGroup>
      </FormControl>

      <FormControl
        errorMsg={errors.tax_id?.message}
        isRequired
        sx={fullWidthStyles}
      >
        <FormLabel fontWeight="normal">
          {t('form.companyTaxInvoice.taxId')}
        </FormLabel>
        <Input
          size="lg"
          placeholder={
            taxIdType === TaxIdType.Thai
              ? t('form.companyTaxInvoice.taxIdPlaceholderThai')
              : t('form.companyTaxInvoice.taxIdPlaceholderForeign')
          }
          maxLength={taxIdType === TaxIdType.Thai ? TAX?.THAI_TAX_ID_LENGTH : undefined}
          {...register('tax_id')}
        />
      </FormControl>

      <FormControl
        errorMsg={errors.name?.message}
        isRequired
        sx={fullWidthStyles}
      >
        <FormLabel fontWeight="normal">
          {t('form.companyTaxInvoice.companyName')}
        </FormLabel>
        <Input
          size="lg"
          placeholder={t('form.companyTaxInvoice.companyNamePlaceholder')}
          {...register('name')}
        />
        <FormHelperText fontSize="xs" color="gray.500">
          {t('form.companyTaxInvoice.companyNameHint')}
        </FormHelperText>
      </FormControl>

      <FormControl sx={fullWidthStyles}>
        <RadioGroup
          onChange={(val) => setValue('branch_type', val as BranchType)}
          value={branchType}
        >
          <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
            {BRANCH_CONFIG.map((option) => (
              <Radio key={option.value} value={option.value}>
                {t(option.labelKey)}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </FormControl>

      {branchType === BranchType.Branch && (
        <FormControl
          errorMsg={errors.branch_name?.message}
          label={t('form.companyTaxInvoice.branchName')}
          isRequired
          sx={fullWidthStyles}
        >
          <Input
            size="lg"
            placeholder={t('form.companyTaxInvoice.branchNamePlaceholder')}
            {...register('branch_name')}
          />
        </FormControl>
      )}

      <FormLabel fontWeight="normal" sx={fullWidthStyles} mt={2}>
        {t('form.companyTaxInvoice.addressDetails')}
      </FormLabel>

      <FormControl
        errorMsg={errors.address?.message}
        isRequired
        sx={fullWidthStyles}
      >
        <FormLabel fontWeight="normal" sx={fullWidthStyles} mt={2}>
          {t('form.companyTaxInvoice.addressNo')}
        </FormLabel>
        <Input
          size="lg"
          placeholder={t('form.companyTaxInvoice.addressNoPlaceholder')}
          {...register('address')}
        />
      </FormControl>

      <FormControl errorMsg={errors.sub_district?.message} isRequired>
        <FormLabel fontWeight="normal" sx={fullWidthStyles} mt={2}>
          {t('form.companyTaxInvoice.subDistrict')}
        </FormLabel>
        <Input
          size="lg"
          placeholder={t('form.companyTaxInvoice.subDistrictPlaceholder')}
          {...register('sub_district')}
        />
      </FormControl>

      <FormControl errorMsg={errors.district?.message} isRequired>
        <FormLabel fontWeight="normal" sx={fullWidthStyles} mt={2}>
          {t('form.companyTaxInvoice.district')}
        </FormLabel>
        <Input
          size="lg"
          placeholder={t('form.companyTaxInvoice.districtPlaceholder')}
          {...register('district')}
        />
      </FormControl>

      <FormControl errorMsg={errors.province?.message} isRequired>
        <FormLabel fontWeight="normal" sx={fullWidthStyles} mt={2}>
          {t('form.companyTaxInvoice.province')}
        </FormLabel>
        <Input
          size="lg"
          placeholder={t('form.companyTaxInvoice.provincePlaceholder')}
          {...register('province')}
        />
      </FormControl>

      <FormControl errorMsg={errors.zip_code?.message} isRequired>
        <FormLabel fontWeight="normal" sx={fullWidthStyles} mt={2}>
          {t('form.companyTaxInvoice.zipCode')}
        </FormLabel>
        <Input
          size="lg"
          placeholder={t('form.companyTaxInvoice.zipCodePlaceholder')}
          {...register('zip_code')}
        />
      </FormControl>
    </Grid>
  )
}
